import ENV from '~/constants/ENV';
import EXTENSION_MESSAGE_TYPES from '~/constants/EXTENSION_MESSAGE_TYPES';

type MessageTypeNames =
  (typeof EXTENSION_MESSAGE_TYPES)[keyof typeof EXTENSION_MESSAGE_TYPES];

interface ICheckResult<T extends MessageTypeNames, U> {
  type: T;
  data?: U;
  error?: string;
}

export interface IUnknownMessage {
  error: string;
}

export interface IDisplay {
  id: string;
  height: number;
  width: number;
  dpiX?: number;
  dpiY?: number;
}

export type ExtensionVersionResult = ICheckResult<
  'EXTENSION_VERSION',
  { version: string }
>;
export type ExternalStorageResult = ICheckResult<
  'EXTERNAL_STORAGE',
  { removableStorageUnitCount: number }
>;
export type DeviceSerialNumberResult = ICheckResult<
  'SERIAL_NUMBER',
  { serialNumber: string }
>;
export type ExtensionDisplayInfoResult = ICheckResult<
  'DISPLAY_INFO',
  IDisplay[]
>;

export interface IExtensionMessageResponse {
  [EXTENSION_MESSAGE_TYPES.DISPLAY_INFO]: ExtensionDisplayInfoResult;
  [EXTENSION_MESSAGE_TYPES.EXTERNAL_STORAGE]: ExternalStorageResult;
  [EXTENSION_MESSAGE_TYPES.SERIAL_NUMBER]: DeviceSerialNumberResult;
  [EXTENSION_MESSAGE_TYPES.EXTENSION_VERSION]: ExtensionVersionResult;
}

const fetchFromExtension = async <T extends MessageTypeNames>(
  messageType: T,
): Promise<IExtensionMessageResponse[T]> => {
  if (!window.chrome?.runtime) {
    throw new Error('Chrome runtime not available');
  }

  const response: IExtensionMessageResponse[T] | IUnknownMessage =
    await window.chrome.runtime.sendMessage(ENV.EXTENSION_ID, messageType);

  if (!response) {
    throw new Error(
      JSON.stringify({
        error: 'No response from extension',
        extensionDetails: window.chrome.runtime?.getManifest?.(),
        lastError: window.chrome.runtime?.lastError,
        messageType,
      }),
    );
  }

  if (!('type' in response)) {
    // Throw error for IUnknownMessage response
    throw new Error(`Unexpected response from extension: ${response.error}`);
  }

  return response;
};

export default fetchFromExtension;
